import {
	API_ENDPOINTS,
	DataTable,
	formatDate,
	FormInput,
	Icon,
	Icons,
	PageLayout,
	route,
	StringHelpers,
	useAuthorisationContext,
	useLangContext,
	replaceKeyWithValue,
	Variants,
	Permissions,
} from 'carrier-fe';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QuizType } from '../../types/quiz';
import { SEARCH_FILTERS } from '../../Constants';

function QuizList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { crud, fields, models } = useLangContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const handleOptionSelect = (action: any, data: QuizType) => {
		const { id = '' } = data;

		const actions: { [key: string]: () => void } = {
			view: () => {
				const searchFilter = searchParams.get('filter[search]');
				if (searchFilter) {
					sessionStorage.setItem(SEARCH_FILTERS.QUIZ, searchFilter);
				}
				navigate(`/training/quiz/${id}`);
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index.title || ':model List ',
					'model',
					crud?.models?.training_quiz || 'Training Quiz'
				)
			),
		[crud, models]
	);

	const items = useMemo(() => {
		if (!hasPermissionAccess(Permissions.ADMIN_TRAINING_QUIZ_STORE))
			return [];

		return [
			{
				label: StringHelpers.title(
					crud?.buttons?.create?.default || 'Create'
				),
				disabled: false,
				onClick: () => navigate('/training/quiz/create'),
				icon: <Icon icon={Icons.ADD} />,
			},
		];
	}, []);

	const colMapping = (data: QuizType) => [
		{
			id: 'name',
			label: StringHelpers.title(fields?.name || 'Name'),
			value: StringHelpers.title(data?.name || '-'),
			sortable: true,
		},
		{
			id: 'modules_count',
			label: StringHelpers.title(fields?.modules || 'Modules'),
			value: String(data?.modules_count) || '-',
			sortable: false,
		},
		{
			id: 'questions_count',
			label: StringHelpers.title(fields?.questions || 'Questions'),
			value: data?.questions_count || '-',
			sortable: true,
		},
		{
			id: 'created_at',
			label: StringHelpers.title(fields?.created_at || 'Created'),
			value: formatDate(data.created_at) || '-',
			sortable: true,
		},
		{
			id: 'actions',
			label: StringHelpers.title(fields?.actions || 'Actions'),
			value: data.permissions.view ? (
				<FormInput
					type="select"
					label={StringHelpers.title(fields?.action || 'Action')}
					placeholder={StringHelpers.title(
						fields?.select || 'Select'
					)}
					name="actions"
					value=""
					onChange={(value) => {
						if (!!value && typeof value === 'string') {
							handleOptionSelect(value, data);
						}
					}}
					options={[
						{
							label: StringHelpers.title(crud?.view || 'View'),
							value: 'view',
						},
					]}
					noMargin
					disabled={!data.permissions.view}
				/>
			) : null,
		},
	];

	return (
		<>
			<PageLayout title={title || 'Training Quizzes'}>
				<DataTable<QuizType>
					routeEndpoint={route(
						API_ENDPOINTS.ADMIN.TRAINING.QUIZ.INDEX
					)}
					colMapping={colMapping}
					items={items}
					incrementPaginationButtons
					canViewArchived
					variant={Variants.Info}
				/>
			</PageLayout>
		</>
	);
}

export default QuizList;
